/**
 * Character system types for data-driven NPC and player character management.
 * Supports modular character assembly: shared skeletons, swappable body meshes,
 * and equipment attachment to specific bones.
 */
import type { CharacterEquipmentLoadout, ItemRarity } from './types/game';
import { resolveItemRarityRank } from './item-instances';
import type { BodyPartModifier, OutfitLoadout, CharacterModelSelection } from './types/character-system';

export function resolveCharacterModelSelection(selection: CharacterModelSelection): {
    characterModelId: string;
    outfit: OutfitLoadout;
    outfitModifiers: Partial<Record<string, BodyPartModifier>>;
} {
    if (typeof selection === 'string') {
        return {
            characterModelId: selection,
            outfit: {},
            outfitModifiers: {},
        };
    }
    return {
        characterModelId: selection.baseModelId,
        outfit: selection.outfit,
        outfitModifiers: selection.outfitModifiers ?? {},
    };
}

/**
 * The asset id behind an item's model and icon (`/assets/items/:id`): its
 * `modelId`, or its item definition id when it has none.
 */
export function resolveItemModelId(item: { readonly itemDefinitionId: string; readonly modelId?: string }): string {
    return item.modelId || item.itemDefinitionId;
}

/**
 * Flattens a player's equipped loadout into the model ids (see
 * `resolveItemModelId`) currently worn/wielded, deduplicated. Used wherever only "which items are
 * equipped" matters (asset pre-loading, network visibility to other
 * clients) rather than which structural loadout slot (weapon/armor/misc)
 * each occupies — resolving an item id back to its bone-attachment slot is
 * a catalog lookup (`EquipmentPiece.slot`), not something this loadout
 * shape itself encodes.
 */
export function resolveEquippedLoadoutSummary(equipped: CharacterEquipmentLoadout): string[] {
    const modelIds = new Set<string>();
    // Persisted characters fill empty slots with placeholder pieces whose `itemDefinitionId` is '',
    // so a truthy piece is not necessarily an occupied slot.
    for (const piece of [...equipped.weapons, equipped.armor, ...equipped.miscellaneous]) {
        if (piece?.itemDefinitionId) {
            modelIds.add(resolveItemModelId(piece));
        }
    }

    return Array.from(modelIds);
}

/** A wielded weapon's rolled tier: what its rarity and upgrade effect is drawn from (#287). */
export interface WieldedItemTier {
    readonly rarity: ItemRarity;
    readonly upgradeLevel: number;
    /** The named item it is, whose own look replaces its rarity's (#295). */
    readonly namedItemId?: string;
}

/**
 * The rolled tier of each weapon wielded, keyed by model id as
 * `resolveEquippedLoadoutSummary` lists it, for drawing its rarity and
 * upgrade effect on the character (#287). A plain copy has none. When both
 * hands hold the same model, the rarer (then more upgraded) copy shows.
 */
export function resolveWieldedItemTiers(equipped: CharacterEquipmentLoadout | undefined): Record<string, WieldedItemTier> {
    const tiers: Record<string, WieldedItemTier> = {};
    for (const piece of equipped?.weapons ?? []) {
        const instance = piece?.instance;
        if (!piece?.itemDefinitionId || !instance) continue;
        const modelId = resolveItemModelId(piece);
        const tier: WieldedItemTier = {
            rarity: instance.rarity,
            upgradeLevel: instance.upgradeLevel,
            ...(instance.namedItemId !== undefined ? { namedItemId: instance.namedItemId } : {}),
        };
        const shown = tiers[modelId];
        const outranks = shown === undefined
            || resolveItemRarityRank(tier.rarity) > resolveItemRarityRank(shown.rarity)
            || (tier.rarity === shown.rarity && tier.upgradeLevel > shown.upgradeLevel);
        if (outranks) tiers[modelId] = tier;
    }
    return tiers;
}

/**
 * Flattens a slot-id -> body-part-id outfit loadout into its body-part ids,
 * deduplicated. Kept separate from `resolveEquippedLoadoutSummary` (rather
 * than merged into one generic "visual items" list everywhere) so callers
 * that need to preload each catalog through its own endpoint (`/assets/items`
 * vs `/assets/body-parts`) can do so directly, without falling back to a
 * try-one-then-the-other guess.
 */
export function listOutfitItemIds(outfit: OutfitLoadout | undefined): string[] {
    if (!outfit) {
        return [];
    }
    const bodyPartIds = new Set<string>();
    Object.values(outfit).forEach((id) => {
        if (id) bodyPartIds.add(id);
    });
    return Array.from(bodyPartIds);
}

/**
 * Flattens both equipment and body-part loadouts into one visual-item id array,
 * used for asset preloading and network visibility. Entity-agnostic: works for
 * both players (equipped + outfit) and NPCs (equipment + outfit).
 */
export function listCharacterVisualItemIds(
    equipped: CharacterEquipmentLoadout | undefined,
    outfit: OutfitLoadout | undefined,
): string[] {
    const itemDefinitionIds = new Set<string>();
    if (equipped) {
        resolveEquippedLoadoutSummary(equipped).forEach((id) => itemDefinitionIds.add(id));
    }
    listOutfitItemIds(outfit).forEach((id) => itemDefinitionIds.add(id));
    return Array.from(itemDefinitionIds);
}
